import { useEffect } from 'react';
import { useSupabaseAuth } from '@/hooks/auth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/sonner';
import { validateCourseCompletionForCertificate } from '@/hooks/auth/progressService';

export const useCertificateNotifications = (courseId?: string, isCompleted?: boolean) => {
  const { user } = useSupabaseAuth();

  useEffect(() => {
    if (!user) return;

    const checkCertificateEligibility = async () => {
      try {
        // If courseId provided, check specific course
        if (courseId && isCompleted) {
          const { data: certificate } = await supabase
            .from('certificates')
            .select('*')
            .eq('user_id', user.id)
            .eq('course_id', courseId)
            .gte('issue_date', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString())
            .maybeSingle();

          if (certificate) {
            toast.success(`🎉 Certificate ready for ${certificate.course_title}!`, {
              description: "Your certificate has been generated and is ready for download.",
              action: {
                label: "View Certificate",
                onClick: () => {
                  const certificateSection = document.getElementById('certificate-section');
                  certificateSection?.scrollIntoView({ behavior: 'smooth' });
                }
              },
              duration: 10000
            });
          }
        } else {
          // Phase 2: STRICT validation - Check all enrolled courses for eligibility
          const { data: enrollments } = await supabase
            .from('user_course_enrollments')
            .select('course_id, progress')
            .eq('user_id', user.id)
            .gte('progress', 75); // 75%+ complete

          if (enrollments && enrollments.length > 0) {
            for (const enrollment of enrollments) {
              // Check if already has certificate (and not revoked)
              const { data: existingCert } = await supabase
                .from('certificates')
                .select('id, revoked')
                .eq('user_id', user.id)
                .eq('course_id', enrollment.course_id)
                .maybeSingle();

              if (!existingCert || existingCert.revoked) {
                // Phase 2: STRICT validation with module completion
                const validation = await validateCourseCompletionForCertificate(
                  user.id,
                  enrollment.course_id
                );

                if (validation.canIssue) {
                  toast.info(`🎓 Certificate Ready: ${enrollment.course_id.replace(/-/g, ' ')}`, {
                    description: `All ${validation.totalModules} modules completed! Generate your certificate now.`,
                    duration: 8000
                  });
                } else if (enrollment.progress >= 90) {
                  const modulesLeft = (validation.totalModules || 0) - (validation.completedModules || 0);
                  toast(`📚 Almost there! ${enrollment.course_id.replace(/-/g, ' ')}`, {
                    description: modulesLeft > 0 
                      ? `Complete ${modulesLeft} more module${modulesLeft > 1 ? 's' : ''} to earn certificate`
                      : validation.reason || "Keep learning to earn your certificate",
                    duration: 6000
                  });
                }
              }
            }
          }
        }
      } catch (error) {
        console.error('Error checking certificate eligibility:', error);
      }
    };

    // Check immediately
    checkCertificateEligibility();

    // Set up periodic checks (every 5 minutes)
    const interval = setInterval(checkCertificateEligibility, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [user, courseId, isCompleted]);
};
